// src/components/TradeStatusModal.jsx
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { apiUrl } from "../config/api";
import { userAuthHeaders } from "../utils/authHeaders";

function TradeStatusModal({ isOpen, onClose, tradeId, tradeDetails, onTimeout }) {
  const [timeLeft, setTimeLeft] = useState(80);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);

  const prevStatusRef = useRef("pending");
  const statusRef = useRef("pending");

  const radius = 70;
  const stroke = 8;
  const normalizedRadius = radius - stroke * 2;
  const circumference = normalizedRadius * 2 * Math.PI;

  useEffect(() => {
    if (!isOpen) return;

    setTimeLeft(80);
    setStatus("pending");
    setLoading(true);
    prevStatusRef.current = "pending";
    statusRef.current = "pending";
  }, [isOpen, tradeId]);

  useEffect(() => {
    if (!isOpen || !tradeId) return;

    const fetchStatus = async () => {
      try {
        const response = await fetch(apiUrl(`/api/trades/${tradeId}/status`), {
          headers: userAuthHeaders(),
        });
        const data = await response.json();

        if (!data.success) {
          setLoading(false);
          return;
        }

        setStatus(data.status);
        statusRef.current = data.status;
        setLoading(false);

        if (data.status === "approved" || data.status === "rejected") {
          setTimeLeft(0);
        } else if (typeof data.remainingTime === "number") {
          setTimeLeft((prev) => Math.min(prev, data.remainingTime));
        }

        if (data.status === "approved" && prevStatusRef.current !== "approved") {
          toast.success("معامله شما با موفقیت تایید شد!");
        } else if (data.status === "rejected" && prevStatusRef.current !== "rejected") {
          toast.error(data.rejectedReason || "معامله رد شد.");
        }

        prevStatusRef.current = data.status;
      } catch (error) {
        console.error("Error fetching status:", error);
        setLoading(false);
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 3000);

    return () => {
      clearInterval(interval);
    };
  }, [isOpen, tradeId]);

  useEffect(() => {
    if (!isOpen) return;

    const timer = setInterval(() => {
      if (statusRef.current === "approved" || statusRef.current === "rejected") {
        return;
      }

      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          if (onTimeout) onTimeout();
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [isOpen, onTimeout]);

  if (!isOpen) return null;

  const strokeDashoffset = circumference - (timeLeft / 80) * circumference;
  const isFinalStatus = status === "approved" || status === "rejected";
  const isBuy = tradeDetails?.mode === "buy";

  const formatNumber = (num) => {
    if (num === null || num === undefined || num === "") return "—";
    return Number(num).toLocaleString("fa-IR");
  };

  let message = "در حال بررسی...";
  let textColor = "#3498db";
  let strokeColor = "#3498db";
  let icon = "⏳";

  if (status === "pending") {
    message = loading ? "در حال بررسی..." : "در انتظار تایید مدیر...";
  } else if (status === "approved") {
    message = "معامله تایید شد!";
    textColor = "#27ae60";
    strokeColor = "#27ae60";
    icon = "✅";
  } else if (status === "rejected") {
    message = "معامله رد شد.";
    textColor = "#e74c3c";
    strokeColor = "#e74c3c";
    icon = "❌";
  }

  const priceColor = isBuy ? "#2e7d32" : "#c62828";
  const priceBgColor = isBuy ? "#e8f5e9" : "#ffebee";
  const priceLabel = isBuy ? "نرخ خرید" : "نرخ فروش";

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="trade-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "400px" }}>
        <div className="trade-modal-header">
          <div className="trade-modal-title">
            <span>وضعیت معامله</span>
          </div>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        {tradeDetails && (
          <div
            style={{
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              padding: "15px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "12px",
                paddingBottom: "12px",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <span style={{ fontWeight: "bold", color: "#333" }}>{tradeDetails.itemName}</span>
              <span
                style={{
                  backgroundColor: priceBgColor,
                  color: priceColor,
                  padding: "4px 10px",
                  borderRadius: "4px",
                  fontSize: "0.85em",
                  fontWeight: "bold",
                }}
              >
                {isBuy ? "خرید از ما" : "فروش به ما"}
              </span>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "12px" }}>
              <div>
                <span style={{ color: "#666", fontSize: "0.85em" }}>وزن</span>
                <div style={{ fontWeight: "bold", color: "#333" }}>
                  {formatNumber(tradeDetails.weight)} گرم
                </div>
              </div>
              <div>
                <span style={{ color: "#666", fontSize: "0.85em" }}>مبلغ کل</span>
                <div style={{ fontWeight: "bold", color: "#333" }}>
                  {formatNumber(tradeDetails.totalPrice)} تومان
                </div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
              <div>
                <span style={{ color: "#666", fontSize: "0.85em" }}>نرخ هر گرم</span>
                <div style={{ fontWeight: "bold", color: priceColor }}>
                  {formatNumber(Math.round(tradeDetails.pricePerGram))} تومان
                </div>
              </div>
              <div>
                <span style={{ color: "#666", fontSize: "0.85em" }}>{priceLabel}</span>
                <div style={{ fontWeight: "bold", color: priceColor }}>
                  {formatNumber(tradeDetails.baseRate ?? tradeDetails.totalPriceValue)} تومان
                </div>
              </div>
            </div>
          </div>
        )}

        <div style={{ padding: "20px" }}>
          {!isFinalStatus ? (
            <>
              <div style={{ position: "relative", width: "160px", height: "160px", margin: "0 auto 20px" }}>
                <svg height={radius * 2} width={radius * 2} style={{ transform: "rotate(-90deg)" }}>
                  <circle
                    stroke="#e6e6e6"
                    fill="transparent"
                    strokeWidth={stroke}
                    r={normalizedRadius}
                    cx={radius}
                    cy={radius}
                  />
                  <circle
                    stroke={strokeColor}
                    fill="transparent"
                    strokeWidth={stroke}
                    strokeDasharray={`${circumference} ${circumference}`}
                    style={{
                      strokeDashoffset,
                      transition: "stroke-dashoffset 0.5s linear",
                      strokeLinecap: "round",
                    }}
                    r={normalizedRadius}
                    cx={radius}
                    cy={radius}
                  />
                </svg>
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    fontSize: "2em",
                    fontWeight: "bold",
                    color: textColor,
                  }}
                >
                  {timeLeft}
                </div>
              </div>
              <p style={{ color: textColor, fontSize: "1.1em", fontWeight: "bold", textAlign: "center" }}>
                {message}
              </p>
            </>
          ) : (
            <>
              <div style={{ fontSize: "4em", margin: "20px 0", textAlign: "center" }}>{icon}</div>
              <p style={{ color: textColor, fontSize: "1.3em", fontWeight: "bold", textAlign: "center" }}>
                {message}
              </p>
            </>
          )}
        </div>

        {isFinalStatus && (
          <button
            className="trade-submit-btn"
            style={{ backgroundColor: textColor, width: "calc(100% - 40px)", margin: "0 20px 20px" }}
            onClick={onClose}
          >
            بستن
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}

export default TradeStatusModal;
